import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { stats, testimonials } from '@/mocks/stats';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      return;
    }

    setFormStatus('loading');
    setFormMessage('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9qqv6snlsngrm4lm49g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormMessage(
          typeof serverMsg === 'string' && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))
            ? '提交失败，请稍后重试'
            : serverMsg,
        );
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={[{ label: '关于我们' }]} />

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                关于海角论坛
              </h1>
              <p className="text-sm text-foreground-500 max-w-2xl mx-auto">
                海角论坛成立于2018年，致力于打造一个开放、友好、高质量的中文网络社区，让每一位用户都能在这里找到志同道合的朋友。
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 mb-16">
              <div>
                <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-4">品牌故事</h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    海角论坛诞生于一群对互联网社区充满热情的年轻人之手。2018年，我们发现中文互联网上缺少一个真正以用户为中心的综合性社区平台——于是海角论坛应运而生。
                  </p>
                  <p>
                    从最初的一个小小论坛，到如今拥有<strong>180万+注册用户</strong>的大型社区，海角论坛始终秉持"真实分享、友善交流"的核心理念。
                    我们相信，每个人都有值得分享的故事，每个问题都能在这里找到答案。
                  </p>
                  <p>
                    海角论坛目前设有八大主题版块：技术交流、生活杂谈、影视娱乐、游戏电竞、学习资料、灌水乐园、摄影美图和音乐分享。
                    无论你是技术达人、文艺青年还是生活爱好者，都能在这里找到属于自己的圈子。
                  </p>
                  <p>
                    在海角论坛，你可以浏览海角论坛官网获取最新入口，通过海角论坛注册加入社区，
                    使用海角论坛登录功能访问你的账号，或收藏海角论坛最新地址以便随时访问。
                  </p>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-4">平台数据</h2>
                <div className="grid grid-cols-2 gap-4">
                  {stats.map((stat) => (
                    <div
                      key={stat.label}
                      className="p-5 bg-background-100 rounded-lg border border-background-200/70 text-center"
                    >
                      <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-3">
                        <i className={`${stat.icon} text-lg text-primary-600`}></i>
                      </div>
                      <span className="block font-heading text-2xl font-bold text-primary-500 mb-1">
                        {stat.value}
                      </span>
                      <span className="text-xs text-foreground-500">{stat.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-16">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-8 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {testimonials.map((t, i) => (
                  <div
                    key={i}
                    className="p-6 bg-background-100 rounded-lg border border-background-200/70"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(t.rating)].map((_, j) => (
                        <div key={j} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-500 text-sm"></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 mb-4 leading-relaxed">{t.content}</p>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-foreground-900">{t.name}</span>
                      <span className="text-xs text-foreground-400">{t.role}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-3">联系我们</h2>
              <p className="text-sm text-foreground-500">如有任何问题或建议，欢迎通过以下方式联系我们</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
              <div className="p-5 bg-background-50 rounded-lg border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-3">
                  <i className="ri-mail-line text-lg text-primary-600"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</h3>
                <p className="text-xs text-foreground-500">support@haijiaoluntan.com</p>
              </div>
              <div className="p-5 bg-background-50 rounded-lg border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-3">
                  <i className="ri-customer-service-2-line text-lg text-primary-600"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 mb-1">在线客服</h3>
                <p className="text-xs text-foreground-500">工作日 9:00 - 18:00</p>
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8"
            >
              <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-6">发送留言</h3>

              {formStatus === 'success' && (
                <div className="mb-6 p-4 bg-accent-100 text-accent-700 rounded-md text-sm">
                  {formMessage}
                </div>
              )}
              {formStatus === 'error' && (
                <div className="mb-6 p-4 bg-accent-100 text-accent-700 rounded-md text-sm">
                  {formMessage}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all duration-200"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all duration-200"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all duration-200"
                  placeholder="请输入留言主题"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-accent-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all duration-200 resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-honeypot"
              />

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-md hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 text-sm whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'loading' ? '发送中...' : '发送留言'}
              </button>
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}